import {Component, inject, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogContent, MatDialogRef} from '@angular/material/dialog';
import {ProductService} from '../../../../services/product.service';
import {THEME_CONFIG} from '../../../../../environments/tokens/theme.token';
import {CdkScrollable} from '@angular/cdk/scrolling';
import {MatButton} from '@angular/material/button';
import {DecimalPipe} from '@angular/common';
import {MatCheckboxModule} from '@angular/material/checkbox';

@Component({
  selector: 'app-order-offer-products-details-dialog',
  templateUrl: './order-offer-products-details-dialog.component.html',
  styleUrl: './order-offer-products-details-dialog.component.scss',
  imports: [CdkScrollable, MatDialogContent, MatButton, DecimalPipe,MatCheckboxModule],
  standalone: true
})
export class OrderOfferProductsDetailsDialogComponent {
  readonly data = inject<{
    product: any;
    rule: any;
    categoryIndex: number;
  }>(MAT_DIALOG_DATA);

  private dialogRef = inject(MatDialogRef<OrderOfferProductsDetailsDialogComponent>);
  private productService = inject(ProductService);
  supplementsByCategory: { [category: string]: any[] } = {};
  protected readonly themeConfig = inject(THEME_CONFIG)

  loading = signal(true);
  error = signal(false);
  product: any = null;

  selectedIngredients: Set<string> = new Set();
  accSelections: Map<string, Map<string, number>> = new Map();
  supplementSelections: Map<string, Set<string>> = new Map();

  ngOnInit(): void {
    const productId = this.data.product?.id;
    if (!productId) {
      this.error.set(true);
      this.loading.set(false);
      return;
    }

    // ✅ FIX — Le parent a déjà chargé le produit complet et l'a passé via data.product.
    // Si les données sont déjà présentes (ingredients/supplements/additions),
    // on les utilise directement sans rappeler l'API.
    const p = this.data.product;
    const isAlreadyFull =
      p.ingredients !== undefined ||
      p.supplements !== undefined ||
      p.additions !== undefined;

    if (isAlreadyFull) {
      this.product = p;
      this.initSelections();
      this.loading.set(false);
      return;
    }

    // Sinon (cas rare où le dialog est ouvert sans fullProduct) → appel API
    this.productService.getProductById(productId).subscribe({
      next: (response: any) => {
        this.product = response?.body?.data || response?.body || null;
        if (!this.product) {
          this.product = this.data.product;
        }
        this.initSelections();
        this.loading.set(false);
      },
      error: () => {
        this.product = this.data.product;
        this.initSelections();
        this.loading.set(false);
      }
    });
  }

  private initSelections(): void {
    (this.product?.ingredients || []).forEach((ing: any) => {
      if (ing?.id) this.selectedIngredients.add(ing.id);
    });

    const additions = this.product?.additions || this.product?.accompaniments || [];
    additions.forEach((group: any) => {
      const key = group?.category?.id || group?.id;
      if (key) this.accSelections.set(key, new Map());
    });

    this.groupSupplementsByCategory();

  }

  private groupSupplementsByCategory(): void {
    this.supplementsByCategory = {};
    (this.product?.supplements || []).forEach((supplement: any) => {
      if (supplement.categories && supplement.categories.length > 0) {
        supplement.categories.forEach((category: string) => {
          if (!this.supplementsByCategory[category]) {
            this.supplementsByCategory[category] = [];
          }
          this.supplementsByCategory[category].push(supplement);
        });
      }
    });
  }

  getSupplementCategories(): string[] {
    return Object.keys(this.supplementsByCategory);
  }

  isIngredientSelected(id: string): boolean {
    return this.selectedIngredients.has(id);
  }

  toggleIngredient(id: string): void {
    if (this.selectedIngredients.has(id)) {
      this.selectedIngredients.delete(id);
    } else {
      this.selectedIngredients.add(id);
    }
  }

  getValidationMessage(): string {
    const filteredAdditions = this.getFilteredAdditions();
    if (!filteredAdditions.length) return '';

    for (const addition of filteredAdditions) {
      if (addition.required) {
        const total = this.getTotalAccQty(addition.id);
        if (total < addition.quantity) {
          const remaining = addition.quantity - total;
          const categoryName = this.getAdditionTitle(addition);
          return `Choisissez "${categoryName}" (${remaining} choix restant${remaining > 1 ? 's' : ''})`;
        }
      }
    }
    return '';
  }

  isConfigurationValid(): boolean {
    const filteredAdditions = this.getFilteredAdditions();
    if (!filteredAdditions.length) return true;

    for (const addition of filteredAdditions) {
      if (addition.required) {
        const total = this.getTotalAccQty(addition.id);
        if (total < addition.quantity) {
          return false;
        }
      }
    }
    return true;
  }
  getFilteredAdditions(): any[] {
    const additions = this.product?.additions || [];
    const sizeId = this.data.rule?.selectedSizeId;
    const sizeName = this.data.rule?.selectedSizeName;

    return additions.filter((addition: any) => {
      if (!addition.sizes && !addition.size && !addition.pricePerSizes) return true;

      if (addition.size?.id === sizeId || addition.size?.name === sizeName) return true;

      if (Array.isArray(addition.sizes)) {
        return addition.sizes.some((s: any) => s.id === sizeId || s.name === sizeName);
      }

      if (Array.isArray(addition.pricePerSizes)) {
        return addition.pricePerSizes.some((ps: any) => ps.size?.id === sizeId || ps.size?.name === sizeName);
      }

      return true;
    });
  }
  getAdditionTitle(addition: any): string {
    if (addition.category?.name) return addition.category.name;
    if (addition.categories?.length > 0) return addition.categories[0];
    return 'Choix';
  }

  getAccQty(additionId: string, itemId: string): number {
    return this.accSelections.get(additionId)?.get(itemId) || 0;
  }

  getTotalAccQty(additionId: string): number {
    let total = 0;
    this.accSelections.get(additionId)?.forEach(qty => total += qty);
    return total;
  }

  increaseAccById(additionId: string, itemId: string, maxQty: number): void {

    if (this.getTotalAccQty(additionId) >= maxQty) return;

    const map = this.accSelections.get(additionId) || new Map();

    map.set(itemId, (map.get(itemId) || 0) + 1);

    this.accSelections.set(additionId, map);

    if (this.getTotalAccQty(additionId) === maxQty) {
      this.onAdditionCompleted(additionId);
    }
  }

  onAdditionCompleted(additionId: string): void {

    const additions = this.product?.additions || [];

    const index = additions.findIndex((a: any) => a.id === additionId);

    const nextAddition = additions[index + 1];

    if (nextAddition) {

      setTimeout(() => {
        const el = document.getElementById('addition-' + nextAddition.id);
        el?.scrollIntoView({behavior: 'smooth', block: 'center'});
      });

    }
  }

  decreaseAccById(additionId: string, itemId: string): void {
    const map = this.accSelections.get(additionId) || new Map();
    const current = map.get(itemId) || 0;
    if (current > 0) {
      map.set(itemId, current - 1);
      this.accSelections.set(additionId, map);
    }
  }

  private selectedSupplementIds: Set<string> = new Set();

  isSupplementChecked(supId: string): boolean {
    return this.selectedSupplementIds.has(supId);
  }

  toggleSupplementById(supplement: any): void {
    if (this.selectedSupplementIds.has(supplement.id)) {
      this.selectedSupplementIds.delete(supplement.id);
    } else {
      this.selectedSupplementIds.add(supplement.id);
    }
  }

  isFree(): boolean {
    const r = this.data.rule?.reduction;
    return r?.type === 'PERCENTAGE' && r?.value === 1;
  }

  getDiscountBadge(): string | null {
    const r = this.data.rule?.reduction;
    if (!r) return null;
    if (r.type === 'PERCENTAGE') {
      if (r.value === 1) return null;
      return `-${Math.round(r.value * 100)}%`;
    }
    if (r.type === 'FIXED_AMOUNT') {
      return `-${(r.value as number).toFixed(2).replace('.', ',')} €`;
    }
    return null;
  }

  getBasePrice(): number {
    const price = this.data.product?.price;

    // Si c'est un string comme "2,00 €"
    if (typeof price === 'string') {
      return parseFloat(price.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
    }

    // Si c'est un objet (pizza avec tailles)
    if (typeof price === 'object' && price !== null) {
      // Cherche la taille via la rule (cat.size)
      const rule = this.data.rule;
      const categories = rule?.categories || [];
      const cat = categories.find((c: any) =>
        (c.products || []).some((p: any) => p.id === this.data.product?.id)
      );
      const sizeName = cat?.size?.name;
      if (sizeName && price[sizeName]) {
        return parseFloat(price[sizeName].replace(/[^\d,]/g, '').replace(',', '.')) || 0;
      }

      // Fallback: prend le premier prix disponible
      const firstKey = Object.keys(price)[0];
      if (firstKey && price[firstKey]) {
        return parseFloat(price[firstKey].replace(/[^\d,]/g, '').replace(',', '.')) || 0;
      }
    }

    return 0;
  }

  getFinalPrice(): number {
    const base = this.getBasePrice();
    const r = this.data.rule?.reduction;
    if (!r) return base;
    if (r.type === 'PERCENTAGE') return Math.floor(base * (1 - r.value) * 100) / 100;
    if (r.type === 'FIXED_AMOUNT') return Math.max(0, base - r.value);
    return base;
  }

  parseSupPrice(price: any): number {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      return parseFloat(price.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
    }
    return 0;
  }

  getSupplementPrice(supplement: any): number {
    const pricePerSizes = supplement?.pricePerSizes;

    // ✅ Pas de tailles → prix direct sur supplement.price
    if (!pricePerSizes || pricePerSizes.length === 0) {
      return this.parseSupPrice(supplement?.price || 0);
    }

    // Priorité 1 : Taille passée depuis le parent
    let sizeName = this.data.rule?.selectedSizeName ||
      this.data.product?.selectedSizeName ||
      (this.product as any)?.selectedSizeName;

    // Priorité 2 : Taille venant de la rule
    if (!sizeName) {
      const categories = this.data.rule?.categories || [];
      const cat = categories.find((c: any) =>
        (c.products || []).some((p: any) => p.id === this.data.product?.id)
      );
      sizeName = cat?.size?.name;
    }

    if (sizeName) {
      const found = pricePerSizes.find((ps: any) => ps.size?.name === sizeName);
      if (found?.price !== undefined) {
        return found.price;
      }
    }

    // Fallback : premier prix disponible dans pricePerSizes
    return pricePerSizes[0]?.price || 0;
  }

  // getSupplementPrice(supplement: any): number {
  //   const pricePerSizes = supplement?.pricePerSizes;
  //   if (!pricePerSizes || pricePerSizes.length === 0) return 0;
  //
  //   // Priorité 1 : Taille passée depuis le parent (la plus fiable)
  //   let sizeName = this.data.rule?.selectedSizeName ||
  //     this.data.product?.selectedSizeName ||
  //     (this.product as any)?.selectedSizeName;
  //
  //   // Priorité 2 : Taille venant de la rule
  //   if (!sizeName) {
  //     const categories = this.data.rule?.categories || [];
  //     const cat = categories.find((c: any) =>
  //       (c.products || []).some((p: any) => p.id === this.data.product?.id)
  //     );
  //     sizeName = cat?.size?.name;
  //   }
  //
  //   if (sizeName) {
  //     const found = pricePerSizes.find((ps: any) => ps.size?.name === sizeName);
  //     if (found?.price !== undefined) {
  //       return found.price;
  //     }
  //   }
  //
  //   // Fallback : premier prix disponible
  //   return pricePerSizes[0]?.price || 0;
  // }
  // getSupplementPrice(supplement: any): number {
  //   // Les prix sont dans pricePerSizes, pas dans price
  //   const pricePerSizes = supplement?.pricePerSizes;
  //   if (!pricePerSizes || pricePerSizes.length === 0) return 0;
  //
  //   // Cherche la taille définie dans la rule (cat.size)
  //   const categories = this.data.rule?.categories || [];
  //   const cat = categories.find((c: any) =>
  //     (c.products || []).some((p: any) => p.id === this.data.product?.id)
  //   );
  //   const sizeName = cat?.size?.name;
  //
  //   if (sizeName) {
  //     const found = pricePerSizes.find((ps: any) => ps.size?.name === sizeName);
  //     if (found) return found.price || 0;
  //   }
  //
  //   // Fallback: premier prix disponible
  //   return pricePerSizes[0]?.price || 0;
  // }
  cancel(): void {
    this.dialogRef.close({action: 'cancel'});
  }

  confirm(): void {
    const removedIngredients = (this.product?.ingredients || [])
      .filter((ing: any) => !this.selectedIngredients.has(ing.id));

    const selectedAccompaniments: any[] = [];
    this.accSelections.forEach((map, additionId) => {
      map.forEach((qty, itemId) => {
        if (qty > 0) {
          const addition = (this.product?.additions || []).find((a: any) => a.id === additionId);
          const item = addition?.items?.find((i: any) => i.id === itemId);
          if (item) selectedAccompaniments.push({...item, quantity: qty, additionId});
        }
      });
    });
    //
    // const selectedSupplements = (this.product?.supplements || [])
    //   .filter((sup: any) => this.selectedSupplementIds.has(sup.id));
    const selectedSupplements = (this.product?.supplements || [])
      .filter((sup: any) => this.selectedSupplementIds.has(sup.id))
      .map((sup: any) => {
        const realPrice = this.getSupplementPrice(sup);   // ← Prix correct selon la taille
        return {
          ...sup,
          price: realPrice,                    // On force le bon prix
          unitPrice: realPrice                 // On garde aussi pour le panier
        };
      });
    this.dialogRef.close({
      action: 'confirm',
      product: this.data.product,
      categoryIndex: this.data.categoryIndex,
      configuration: {
        removedIngredients,
        selectedAccompaniments,
        selectedSupplements,
      }
    });
  }

}
